import { ConfirmDialog } from '@/components/dialog/confirm-dialog';
import { Alert } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { useMcpConnectionAction } from '@/hooks/actions/use-mcp-actions';
import { Badge, Box, Button, Collapsible, Heading, HStack, Stack, Text } from '@chakra-ui/react';
import { McpClientLogo } from '@/pages/mcp/McpClientLogo';
import type { McpConnection, McpScope } from '@fluenceur/types';
import { useRef, useState } from 'react';
import { NavArrowDown } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { ConnectedAssistantActivity } from './ConnectedAssistantActivity';
import { formatAssistantDate } from './format-assistant-date';

/** One live connection: the assistants page never lists a revoked one. */
export function ConnectedAssistant({
  connection,
  suspended = false,
}: {
  connection: McpConnection;
  /** The plan of the connection's organisation no longer includes connected assistants. */
  suspended?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [brandIds, setBrandIds] = useState(connection.brandIds);
  const [scopes, setScopes] = useState<McpScope[]>(connection.scopes);
  const [pending, setPending] = useState(false);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const submitting = useRef(false);
  const mutation = useMcpConnectionAction(connection.id);
  const changed =
    brandIds.length !== connection.brandIds.length || scopes.length !== connection.scopes.length;
  const removalRevokes = brandIds.length === 0 || !scopes.includes('fluenceur:read');
  const formatDate = (value: string) => formatAssistantDate(value, i18n.language);
  const save = async (revoke: boolean) => {
    if (submitting.current) return;
    submitting.current = true;
    setPending(true);
    try {
      await mutation.mutateAsync(revoke ? null : { brandIds, scopes });
    } catch {
      /* The mutation surfaces translated feedback; retain the selected reduction. */
    } finally {
      submitting.current = false;
      setPending(false);
    }
  };
  return (
    <Box borderWidth="1px" borderRadius="lg" p={{ base: 4, md: 6 }}>
      <Stack gap={5}>
        <Box>
          <HStack gap={3} mb={3}>
            <McpClientLogo provider={connection.client.provider} />
            <Heading as="h2" size="md">
              {connection.client.name}
            </Heading>
          </HStack>
          <HStack gap={2} mt={2}>
            <Text color="fg.muted" fontSize="sm">
              {t('mcp.settings.connectedAt', { date: formatDate(connection.createdAt) })}
            </Text>
            {suspended && (
              <Badge colorPalette="orange" variant="subtle" size="sm">
                {t('planSuspended.mcp.badge')}
              </Badge>
            )}
          </HStack>
          <Text color="fg.muted" fontSize="sm">
            {connection.lastUsedAt
              ? t('mcp.settings.lastUsed', { date: formatDate(connection.lastUsedAt) })
              : t('mcp.settings.neverUsed')}
          </Text>
        </Box>
        <Box as="fieldset" border="none" p={0}>
          <Text as="legend" fontWeight="semibold" mb={3}>
            {t('mcp.consent.brands')}
          </Text>
          <Stack gap={3}>
            {connection.brands.map((brand) => (
              <Checkbox
                key={brand.id}
                disabled={pending}
                checked={brandIds.includes(brand.id)}
                onCheckedChange={({ checked }) =>
                  setBrandIds((current) =>
                    checked === true
                      ? [...current, brand.id]
                      : current.filter((id) => id !== brand.id)
                  )
                }
              >
                {brand.name}
              </Checkbox>
            ))}
          </Stack>
        </Box>
        <Box as="fieldset" border="none" p={0}>
          <Text as="legend" fontWeight="semibold" mb={3}>
            {t('mcp.consent.capabilities')}
          </Text>
          <Stack gap={3}>
            {connection.scopes.map((scope) => (
              <Checkbox
                key={scope}
                disabled={pending}
                checked={scopes.includes(scope)}
                onCheckedChange={({ checked }) =>
                  setScopes((current) =>
                    checked === true
                      ? [...current, scope]
                      : current.filter((item) => item !== scope)
                  )
                }
              >
                {t(`mcp.scopes.${scope.replace(':', '_')}`)}
              </Checkbox>
            ))}
          </Stack>
        </Box>
        <Text color="fg.muted" fontSize="sm">
          {t('mcp.settings.reconsent')}
        </Text>
        {removalRevokes && <Alert status="warning" title={t('mcp.settings.removalRevokes')} />}
        {mutation.isError && <Alert status="error" title={t('mcp.settings.mutationError')} />}
        <Collapsible.Root
          open={activityOpen}
          onOpenChange={({ open }) => setActivityOpen(open)}
          lazyMount
          unmountOnExit
          borderTopWidth="1px"
          pt={4}
        >
          <Collapsible.Trigger asChild>
            <Button variant="ghost" size="sm" px={0} gap={2}>
              {t('mcp.settings.activity.title')}
              <Box
                as="span"
                aria-hidden="true"
                transition="transform 0.2s"
                transform={activityOpen ? 'rotate(180deg)' : undefined}
              >
                <NavArrowDown width={16} height={16} />
              </Box>
            </Button>
          </Collapsible.Trigger>
          <Collapsible.Content pt={3}>
            <ConnectedAssistantActivity connectionId={connection.id} enabled={activityOpen} />
          </Collapsible.Content>
        </Collapsible.Root>
        <Stack direction={{ base: 'column', sm: 'row' }} gap={3}>
          <Button
            disabled={!changed || pending}
            loading={pending}
            onClick={() => (removalRevokes ? setConfirmingDisconnect(true) : void save(false))}
          >
            {t('mcp.settings.save')}
          </Button>
          <Button
            variant="outline"
            colorPalette="red"
            disabled={pending}
            onClick={() => void save(true)}
          >
            {t('mcp.settings.revoke')}
          </Button>
        </Stack>
      </Stack>
      <ConfirmDialog
        isOpen={confirmingDisconnect}
        title={t('mcp.settings.confirmDisconnect.title', { client: connection.client.name })}
        description={t('mcp.settings.confirmDisconnect.body')}
        confirmText={t('mcp.settings.confirmDisconnect.confirm')}
        confirmColorPalette="red"
        onCancel={() => setConfirmingDisconnect(false)}
        onConfirm={() => {
          setConfirmingDisconnect(false);
          void save(true);
        }}
      />
    </Box>
  );
}
